import { Link } from "react-router-dom";
import { useEffect } from "react";
import QuizEmbed from "@/components/feature/QuizEmbed";
import {
  countryProfiles,
  comparisonRows,
  keyQuestions,
  type CountryProfile,
  type ComparisonRow,
  type KeyQuestion,
} from "@/mocks/countries";

function FlagEmoji({ code }: { code: string }) {
  const flagMap: Record<string, string> = {
    HU: "\uD83C\uDDED\uD83C\uDDFA",
    TR: "\uD83C\uDDF9\uD83C\uDDF7",
    PL: "\uD83C\uDDF5\uD83C\uDDF1",
    ES: "\uD83C\uDDEA\uD83C\uDDF8",
  };
  return <span className="text-2xl mr-2">{flagMap[code] || ""}</span>;
}

function CountryCard({ country }: { country: CountryProfile }) {
  return (
    <article className="bg-white rounded-lg overflow-hidden border border-background-200/70">
      <div className="relative h-[180px] w-full overflow-hidden">
        <img
          src={country.image}
          alt={`${country.name} cityscape`}
          width={country.imageWidth}
          height={country.imageHeight}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <FlagEmoji code={country.flag} />
          <h3 className="text-lg font-bold text-foreground-950">
            {country.name} ({country.cities})
          </h3>
          {country.isPremium && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-accent-100 text-accent-900 border border-accent-200">
              Premium EU
            </span>
          )}
        </div>
        <p className="text-sm leading-relaxed text-foreground-700 mb-4">
          {country.description}
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {country.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-900"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="rounded-md bg-primary-50 border border-primary-200/70 p-3">
          <p className="text-sm text-foreground-800 leading-relaxed">
            <strong className="text-foreground-950">Best for:</strong>{" "}
            {country.bestFor}
          </p>
        </div>
      </div>
    </article>
  );
}

function ComparisonCell({
  value,
  status,
}: {
  value: string;
  status?: "check" | "partial" | "neutral";
}) {
  if (!status || status === "neutral") {
    return <span className="text-sm text-foreground-700">{value}</span>;
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-sm font-medium ${
        status === "check"
          ? "text-primary-700"
          : "text-secondary-700"
      }`}
    >
      <span
        className={`w-4 h-4 flex items-center justify-center rounded-full text-xs ${
          status === "check"
            ? "bg-primary-100 text-primary-700"
            : "bg-secondary-100 text-secondary-700"
        }`}
      >
        {status === "check" ? "\u2714" : "\u223C"}
      </span>
      {value}
    </span>
  );
}

function ComparisonTable() {
  return (
    <div className="overflow-x-auto -mx-4 md:mx-0">
      <table className="w-full min-w-[800px] border-collapse">
        <thead>
          <tr className="bg-[#182940]">
            <th className="text-left px-4 py-3.5 text-sm font-semibold text-white/90 rounded-tl-lg">
              Factor
            </th>
            <th className="text-left px-4 py-3.5 text-sm font-semibold text-white/90">
              <span className="mr-1">🇭🇺</span>Hungary
            </th>
            <th className="text-left px-4 py-3.5 text-sm font-semibold text-white/90">
              <span className="mr-1">🇹🇷</span>Turkey
            </th>
            <th className="text-left px-4 py-3.5 text-sm font-semibold text-white/90">
              <span className="mr-1">🇵🇱</span>Poland
            </th>
            <th className="text-left px-4 py-3.5 text-sm font-semibold text-white/90 rounded-tr-lg">
              <span className="mr-1">🇪🇸</span>Spain
            </th>
          </tr>
        </thead>
        <tbody>
          {comparisonRows.map((row, idx) => (
            <tr
              key={row.factor}
              className={
                idx % 2 === 1
                  ? "bg-background-100/50"
                  : "bg-white"
              }
            >
              <td className="px-4 py-3.5 text-sm font-semibold text-foreground-900">
                {row.factor}
              </td>
              <td className="px-4 py-3.5">
                <ComparisonCell value={row.hungary} status={row.hungaryStatus} />
              </td>
              <td className="px-4 py-3.5">
                <ComparisonCell value={row.turkey} status={row.turkeyStatus} />
              </td>
              <td className="px-4 py-3.5">
                <ComparisonCell value={row.poland} status={row.polandStatus} />
              </td>
              <td className="px-4 py-3.5">
                <ComparisonCell value={row.spain} status={row.spainStatus} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuestionCard({ question }: { question: KeyQuestion }) {
  return (
    <div className="bg-white rounded-lg border border-background-200/70 p-5 md:p-6">
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-4">
        <span className="w-5 h-5 flex items-center justify-center">
          <i className={`${question.icon} text-lg`}></i>
        </span>
      </div>
      <h4 className="text-base font-semibold text-foreground-950 mb-2">
        {question.question}
      </h4>
      <p className="text-sm leading-relaxed text-foreground-700">
        {question.answer}
      </p>
    </div>
  );
}

export default function CountryFit() {
  useEffect(() => {
    const originalTitle = document.title;
    const originalDesc = document.querySelector('meta[name="description"]')?.getAttribute("content") || "";
    const originalOgUrl = document.querySelector('meta[property="og:url"]')?.getAttribute("content") || "";
    const originalOgTitle = document.querySelector('meta[property="og:title"]')?.getAttribute("content") || "";
    const originalOgDesc = document.querySelector('meta[property="og:description"]')?.getAttribute("content") || "";

    document.title = "Country Fit – Hungary, Poland, Turkey & Spain Compared | Dental Safety Abroad";
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) {
      descMeta.setAttribute(
        "content",
        "Compare Hungary, Poland, Turkey and Spain for dental treatment abroad. Costs, standards, treatment types, travel considerations, and quality indicators — all in one place."
      );
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", "https://dentalsafetyabroad.com/country-fit");
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", "Country Fit – Hungary vs Poland vs Turkey vs Spain for Dental Treatment");
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", "Independent comparison of the four most popular dental tourism destinations for UK patients.");

    // Inject structured data
    const scriptId = "country-fit-structured-data";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Country Fit – Hungary, Poland, Turkey & Spain Compared",
        description:
          "Independent, side-by-side comparison of Hungary, Poland, Turkey and Spain for dental treatment abroad. Costs, standards, travel, and quality.",
        url: "https://dentalsafetyabroad.com/country-fit",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Hungary",
              description:
                "EU-regulated dental tourism capital for 30+ years. Best for implants and full-arch work.",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Turkey",
              description:
                "Most price-competitive destination. Best for veneers and cosmetic dentistry.",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Poland",
              description:
                "Excellent balance of EU standards, transparent pricing, and short travel.",
            },
            {
              "@type": "ListItem",
              position: 4,
              name: "Spain",
              description:
                "Premium EU-regulated destination with easiest UK travel and strong aftercare.",
            },
          ],
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      document.title = originalTitle;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", originalDesc);
      const ou = document.querySelector('meta[property="og:url"]');
      if (ou) ou.setAttribute("content", originalOgUrl);
      const ot = document.querySelector('meta[property="og:title"]');
      if (ot) ot.setAttribute("content", originalOgTitle);
      const od = document.querySelector('meta[property="og:description"]');
      if (od) od.setAttribute("content", originalOgDesc);
      const sd = document.getElementById(scriptId);
      if (sd) sd.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Page Hero */}
      <section className="bg-[#182940] pt-20 md:pt-28">
        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
          <nav aria-label="Breadcrumb" className="mb-4">
            <ol className="flex items-center gap-2 text-sm text-white/60">
              <li>
                <Link to="/" className="hover:text-white/90 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <span className="text-white/40">›</span>
              </li>
              <li>
                <span className="text-white/90" aria-current="page">
                  Country Fit
                </span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3">
            Hungary, Poland, Turkey &amp; Spain:
            <br />
            Find Your Country Fit
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-3xl leading-relaxed">
            An independent, side-by-side comparison to help you understand which
            of our four destinations best matches your treatment, budget, and risk
            profile.
          </p>
        </div>
      </section>

      {/* Destination Profiles */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="mb-10 md:mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
            Destination Profiles
          </span>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950">
            A Closer Look at Each Country
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {countryProfiles.map((country) => (
            <CountryCard key={country.id} country={country} />
          ))}
        </div>
      </section>

      {/* Comparison Matrix */}
      <section id="comparison" className="bg-background-100/50">
        <div className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
          <div className="mb-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
              Side-by-Side Comparison
            </span>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-3">
              The Full Comparison Matrix
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              Key factors to consider when choosing your destination. All
              estimates are indicative; verify with individual clinics.
            </p>
          </div>

          <ComparisonTable />

          {/* Disclaimer */}
          <div className="mt-6 rounded-lg bg-accent-50 border border-accent-200/70 p-4 md:p-5">
            <p className="text-sm text-foreground-800 leading-relaxed">
              <strong className="text-foreground-950">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1">⚠️</span>
                Important note:
              </strong>{" "}
              All costs are approximate and based on general market research.
              Individual clinic prices vary significantly. This comparison is for
              general guidance only — not clinical advice. Always obtain a
              personalised written quote before making any decision. See our full{" "}
              <Link
                to="/legal"
                className="text-primary-600 hover:text-primary-700 underline underline-offset-2"
              >
                disclaimer
              </Link>
              .
            </p>
          </div>

          {/* Key Questions */}
          <div className="mt-12 md:mt-16">
            <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 md:mb-8">
              Choosing the Right Destination: Key Questions to Ask Yourself
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {keyQuestions.map((q) => (
                <QuestionCard key={q.question} question={q} />
              ))}
            </div>
          </div>

          {/* Quiz prompt */}
          <div className="mt-8 md:mt-10 rounded-lg bg-primary-50 border border-primary-200/70 p-4 md:p-5">
            <p className="text-sm text-foreground-800 leading-relaxed">
              <strong className="text-foreground-950">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1">💡</span>
                Not sure?
              </strong>{" "}
              Take our free{" "}
              <Link
                to="/quiz"
                className="text-primary-600 hover:text-primary-700 underline underline-offset-2 font-medium"
              >
                Destination Quiz
              </Link>{" "}
              — six quick questions that match your specific situation to the most
              suitable country from all four destinations.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Quiz */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
              Interactive Quiz
            </span>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-3">
              Still Not Sure? Let&apos;s Match You
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              Answer six quick questions and we&apos;ll tell you which of Hungary, Poland, Turkey, or Spain best fits your treatment type, budget, and priorities.
            </p>
          </div>
          <QuizEmbed />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#182940]">
        <div className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3">
            Still Not Sure Which Country Fits You?
          </h2>
          <p className="text-base text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
            Take our free destination quiz — six questions that match your
            treatment type, budget, and risk tolerance to the right country across
            all four destinations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/quiz"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Take the Free Quiz
              <span className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
            <Link
              to="/checklist"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md border border-white/30 text-white hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              Get the Red Flags Checklist
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}